/**
 * Main Theme JavaScript
 * Initializes all modules and handles theme-wide functionality
 */

// Initialize managers when DOM is ready (only if classes are available)
const initTheme = () => {
    // Check if all required classes are available
    if (typeof WindowManager === 'undefined' ||
        typeof BootManager === 'undefined' ||
        typeof InstallManager === 'undefined' ||
        typeof EmailCaptureManager === 'undefined' ||
        typeof ResponsiveManager === 'undefined') {
        console.warn('Smash OS: Manager classes not loaded yet, retrying...');
        setTimeout(initTheme, 100);
        return;
    }

    // Initialize managers (only once)
    if (!window.smashOS) {
        window.smashOS = {
            windowManager: new WindowManager(),
            bootManager: new BootManager(),
            installManager: new InstallManager(),
            emailCaptureManager: new EmailCaptureManager(),
            responsiveManager: new ResponsiveManager(),

            /**
             * Show desktop UI (logo, icons, taskbar) after wallpaper loads
             * This ensures no flash - elements only appear after wallpaper is confirmed loaded
             */
            showDesktopUI: function () {
                const desktop = document.getElementById('desktop');
                const desktopLogo = document.getElementById('desktop-logo');
                const desktopIcons = document.getElementById('desktop-icons');
                const desktopCartIcon = document.getElementById('desktop-cart-icon');
                const taskbar = document.getElementById('taskbar');

                if (!desktop) return;

                // Ensure all UI elements are hidden (CSS handles this, but remove visible class if present)
                if (desktopLogo) {
                    desktopLogo.classList.remove('visible');
                }
                if (desktopIcons) {
                    desktopIcons.classList.remove('visible');
                }
                if (desktopCartIcon) {
                    desktopCartIcon.classList.remove('visible');
                }
                if (taskbar) {
                    taskbar.classList.remove('visible');
                }
                desktop.classList.remove('visible');

                // Wait for wallpaper to be visible before showing UI elements
                let checkAttempts = 0;
                const MAX_CHECK_ATTEMPTS = 100; // ~5s at 50ms
                const revealDesktop = () => {
                    desktop.style.setProperty('display', 'grid', 'important');
                    desktop.classList.add('visible');
                    requestAnimationFrame(() => {
                        if (desktopLogo) desktopLogo.classList.add('visible');
                        if (desktopIcons) desktopIcons.classList.add('visible');
                        if (desktopCartIcon) desktopCartIcon.classList.add('visible');
                        if (taskbar) taskbar.classList.add('visible');
                    });
                };
                const checkWallpaperAndShow = () => {
                    const bgImage = window.getComputedStyle(desktop).backgroundImage;
                    const hasWallpaper = bgImage && bgImage !== 'none' && bgImage !== 'rgba(0, 0, 0, 0)' && bgImage !== 'initial';
                    const styleBgImage = desktop.style.backgroundImage;
                    const hasStyleWallpaper = styleBgImage && styleBgImage !== 'none' && styleBgImage !== '';

                    if (hasWallpaper || hasStyleWallpaper) {
                        revealDesktop();
                        return;
                    }
                    checkAttempts += 1;
                    if (checkAttempts >= MAX_CHECK_ATTEMPTS) {
                        console.warn('[Smash OS] Wallpaper never resolved, revealing desktop anyway');
                        revealDesktop();
                        return;
                    }
                    setTimeout(checkWallpaperAndShow, 50);
                };

                setTimeout(checkWallpaperAndShow, 100);
            }
        };

        // Initialize responsive manager
        window.smashOS.responsiveManager.init();

        // Apply theme preference from localStorage
        const themePreference = localStorage.getItem('smash_theme_preference');
        if (themePreference) {
            document.documentElement.setAttribute('data-theme', themePreference);
        }

        // Desktop stays hidden until boot/install logic determines what to show
        // Don't show desktop yet - it will be shown by bootManager or installManager
        // CSS handles the hiding, we just ensure the visible class is not present
        const desktop = document.getElementById('desktop');
        if (desktop) {
            desktop.classList.remove('visible');
            desktop.style.backgroundColor = '#000000';
            // Don't apply background image yet - will be applied by bootManager or installManager
        }

        // Step 1: Check localStorage values
        const lastBootTimestamp = localStorage.getItem('smash_last_boot_timestamp');
        const installCompleted = localStorage.getItem('smash_install_completed') === 'true';
        const shouldShowBoot = window.smashOS.bootManager.shouldShowBoot();

        // Step 2: Decision tree based on localStorage
        if (!lastBootTimestamp && !installCompleted) {
            // 2.1: No values - show boot screen
            window.smashOS.bootManager.showBoot();
        } else if (lastBootTimestamp && !installCompleted) {
            // 2.2: Boot timestamp exists but no install - set localStorage and show desktop
            // Remove boot sequence from DOM since we're showing desktop
            window.smashOS.bootManager.removeBootSequenceFromDOM();
            // Set localStorage values (install manager is deactivated)
            window.smashOS.bootManager.setInstallLocalStorage();
            window.smashOS.bootManager.showDesktop();
            window.smashOS.showDesktopUI();
            // Open default windows after desktop is shown
            setTimeout(() => {
                if (window.smashOS.windowManager) {
                    window.smashOS.windowManager.openDefaultWindows();
                }
            }, 500);
            // Install manager is deactivated - uncomment below to re-enable:
            /*
            if (window.smashOS.installManager.shouldShowInstall()) {
                window.smashOS.installManager.showInstallWizard();
            }
            */
        } else if (installCompleted && !shouldShowBoot) {
            // 2.3: Both exist and boot hasn't expired - show desktop (after wallpaper loads)
            // Remove boot sequence from DOM since it's not needed
            window.smashOS.bootManager.removeBootSequenceFromDOM();
            window.smashOS.bootManager.showDesktop();
            window.smashOS.showDesktopUI();
            // Open default windows after desktop is shown
            setTimeout(() => {
                if (window.smashOS.windowManager) {
                    window.smashOS.windowManager.openDefaultWindows();
                }
            }, 500);
        } else if (installCompleted && shouldShowBoot) {
            // 2.5: Install exists but boot expired - show boot then desktop
            window.smashOS.bootManager.showBoot();
            // Desktop will be shown after boot completes (handled in bootManager)
        }

        // Load theme settings
        loadThemeSettings();

        // Initialize scrollbar states for all scrollbars
        if (window.smashOS.windowManager) {
            setTimeout(() => {
                window.smashOS.windowManager.initScrollbarStates();
            }, 100);
        }
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTheme);
} else {
    initTheme();
}

/**
 * Load theme settings from Shopify
 */
function loadThemeSettings() {
    console.log('[Theme] loadThemeSettings called');

    // Get settings from JSON script tag (more reliable than theme object)
    const settingsScript = document.getElementById('theme-settings');
    let settings = {};

    if (settingsScript) {
        try {
            settings = JSON.parse(settingsScript.textContent);
            console.log('[Theme] Settings loaded from script tag:', settings);
        } catch (e) {
            console.error('[Theme] Failed to parse settings from script tag:', e);
        }
    } else {
        console.warn('[Theme] theme-settings script tag not found');
    }

    // Also try theme object as fallback
    if (typeof theme !== 'undefined' && theme.settings) {
        console.log('[Theme] theme.settings also available, merging...');
        settings = Object.assign(settings, theme.settings);
    }

    // Expose all settings to window.smashOS.themeSettings for use in other modules
    if (!window.smashOS) {
        window.smashOS = {};
    }
    window.smashOS.themeSettings = settings;

    console.log('[Theme] Exposed themeSettings:', window.smashOS.themeSettings);
    console.log('[Theme] Menu collection item 1 label:', window.smashOS.themeSettings.menu_collection_item_1_label);
    console.log('[Theme] Menu collection item 1 url:', window.smashOS.themeSettings.menu_collection_item_1_url);
    console.log('[Theme] Collections list:', window.smashOS.themeSettings.collections_list);
    console.log('[Theme] Collections list title:', window.smashOS.themeSettings.collections_list_title);

    // Configure boot manager (get from theme object if available)
    if (window.smashOS.bootManager) {
        const bootEnabled = (typeof theme !== 'undefined' && theme.settings && theme.settings.boot_enabled !== false) || true;
        const bootIntervalDays = (typeof theme !== 'undefined' && theme.settings && theme.settings.boot_interval_days) || 1;
        window.smashOS.bootManager.init({
            enabled: bootEnabled,
            intervalDays: bootIntervalDays
        });
    }
}

